import type { NextPage, GetServerSidePropsContext } from 'next';
import { useThrottleEffect } from 'ahooks';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import css from './index.module.scss';
import PageLayout from '@/components/PageLayout';
import { redirectTo404 } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
// 本页面新增模块 部分使用会员页
import JoinEcoclub from '@/components/Membership/JoinEcoclub';
import EuBenefits from '@/components/Membership/EuBenefits';
import WhatsEcoCredits from '@/components/Membership/WhatsEcoCredits';
import EcoAnchor from '@/components/Membership/EcoAnchor';
import WaysToEarnPoints from '@/components/Membership/WaysToEarnPoints';
import EcoCreditRules from '@/components/Membership/EcoCreditRules';
// 使用原来会员页组件和接口
import RewardsRedeemed from '@/components/EcoCredits/RewardsRedeemed';
import PointsActivity from '@/components/EcoCredits/PointsActivity';
import Faq from '@/components/EcoCredits/Faq';
import GrabEcoCreditsBonus from '@/components/EcoCredits/GrabEcoCreditsBonus';
import FullScreenModal from '@/components/EcoCredits/FullScreenModal';
import { EcoProvider } from '@/components/EcoCredits/context';
import { injectNotes } from '@/components/EcoCredits/utils';
import { getRewardUserInfo, getRewardEarnRule, getRewardHistory, getIssuedCoupons, getRewardsActivity, getDiscountCouponList } from '@/service';
import SEOlinksA from '@/components/SEOlinksA';
import seoconfig from '@/seoconfig/membership';

const EcoCredits: NextPage<any> = ({ isLogin, SEO }: any) => {
    const { useLang } = useGlobal();
    const { t } = useLang('ecocredits');
    const { locale, route } = useRouter();
    const [rewardUserInfo, setRewardUserInfo] = useState({});
    const [rewardEarnRule, setRewardEarnRule] = useState([]);
    const [issuedCoupon, setIssuedCoupon] = useState<any[]>([]);
    const [pointsActivity, setPointsActivity] = useState([]);
    const [rewardsActivity, setRewardsActivity] = useState([]);
    const [discountCouponList, setDiscountCouponList] = useState<any[]>([]);
    const [firstIn, setFirstIn] = useState(true);
    const [showPage, setShowPage] = useState('basic'); // basic / history_modal / coupon_modal / points_modal / bouns_modal
    const { timezoneList } = useMemberTimeStore();
    /**
     * 查询账户积分详情
     */
    const queryRewardUserInfo = async () => {
        const res = await getRewardUserInfo(locale as string);
        if (res?.code === '0') {
            setRewardUserInfo(res.data || {});
            return;
        }
    };

    /**
     * 查询issued优惠券兑换记录
     */
    const queryIssuedRewards = async () => {
        const res = await getIssuedCoupons(locale as string);
        if (res?.code === '0') {
            const coupons = injectNotes(res?.data || [], t, locale as string, timezoneList);
            setIssuedCoupon(coupons || []);
        }
    };

    /**
     * 查询查询Rewards Activity 优惠券使用流水
     */
    const queryRewardsActivity = async () => {
        const res = await getRewardsActivity(locale as string);
        if (res?.code === '0') {
            setRewardsActivity(res.data?.content || []);
        }
    };

    /**
     * 查询积分赚取规则列表
     */
    const queryRewardEarnRule = async () => {
        const res = await getRewardEarnRule(locale as string);
        if (res?.code === '0') {
            setRewardEarnRule(res.data || []);
        }
    };

    /**
     * 用户积分历史记录
     */
    const queryRewardHistory = async () => {
        const res = await getRewardHistory(locale as string);
        if (res?.code === '0') {
            setPointsActivity(res.data?.content || []);
        }
    };

    /**
     * 福利中心
     */
    const queryDiscountCouponList = async () => {
        const res = await getDiscountCouponList(locale as string);
        if (res?.code === '0') {
            const coupons = injectNotes(res.data, t, locale as string, timezoneList);
            setDiscountCouponList(coupons || []);
            const isHasUnUsedCoupons = coupons?.some((coupon: any) => coupon?.receiveStatus === 1 || !coupon?.receiveStatus);
            firstIn && isHasUnUsedCoupons && setShowPage('bouns_modal');
            setFirstIn(false);
        }
    };

    const openModal = (key: string) => {
        setShowPage(key);
    };

    const closeModal = () => {
        setShowPage('basic');
    };

    /**
     * 数据请求总和
     */
    const initData = () => {
        if (!isLogin) return;
        queryRewardEarnRule();
        queryRewardUserInfo();
        queryIssuedRewards();
        queryDiscountCouponList();

        queryRewardsActivity();
        queryRewardHistory();
    };
    /**
     * 刷新数据
     */
    const refershData = () => {
        if (!isLogin) return;
        queryRewardEarnRule();
        queryRewardUserInfo();
    };

    const jumpView = () => {
        if (typeof window !== 'undefined') {
            // 因为需要等各组件请求数据回来之后 才能撑起组件高度 定位到对应位置 所以延后300ms执行
            setTimeout(() => {
                document.getElementById(window.location.hash.replace('#', ''))?.scrollIntoView({
                    behavior: 'smooth',
                });
            }, 300);
        }
    };

    useThrottleEffect(() => {
        initData();
        jumpView();
    }, []);

    return (
        <>
            <style jsx global>
                {`
                    .ant-message {
                        z-index: 9999999;
                    }
                    .ant-modal-root {
                        z-index: 2102;
                    }
                `}
            </style>
            <SEO title={t('seo_title') || t('join_title')} description={t('seo_desc')} />
            <SEOlinksA locale={String(locale)} list={seoconfig.include} pathname={seoconfig.url} />
            <PageLayout hideFooter={showPage !== 'basic'} isNoFixed>
                <EcoProvider>
                    <div className={css.ecocredits}>
                        <JoinEcoclub isLogin={isLogin} rewardUserInfo={rewardUserInfo} openModal={openModal} refershData={refershData} />
                        <EcoAnchor isLogin={isLogin} />
                        <WhatsEcoCredits />
                        <EuBenefits isLogin={isLogin} />
                        <div style={{ background: '#F5F5F5' }} id="ECOCREDIT_RULES">
                            <EcoCreditRules
                                isLogin={isLogin}
                                refershData={refershData}
                                rewardUserInfo={rewardUserInfo}
                                setShowPage={setShowPage}
                                pathUrl={`/${locale}${route}`}
                            />
                        </div>
                        <WaysToEarnPoints isLogin={isLogin} rewardEarnRule={rewardEarnRule} refershData={refershData} />
                        <Faq />
                    </div>

                    {showPage?.startsWith('coupon_modal') && (
                        <FullScreenModal
                            visible={showPage?.startsWith('coupon_modal')}
                            className={showPage}
                            style={{ background: '#f8f8f8' }}
                            closeModal={closeModal}
                            afterOpen={() => {
                                queryIssuedRewards();
                            }}
                        >
                            <RewardsRedeemed
                                modal
                                defaultSelectKey={(showPage?.includes('$') && showPage?.split('$').pop()) || '1'}
                                issuedCoupon={issuedCoupon}
                            />
                        </FullScreenModal>
                    )}

                    {showPage === 'history_modal' && (
                        <FullScreenModal
                            visible={showPage === 'history_modal'}
                            className={showPage}
                            closeModal={closeModal}
                            afterOpen={() => {
                                queryRewardsActivity();
                                queryRewardHistory();
                            }}
                        >
                            <PointsActivity modal pointsActivity={pointsActivity} rewardsActivity={rewardsActivity} />
                        </FullScreenModal>
                    )}
                    <FullScreenModal
                        visible={showPage === 'bouns_modal'}
                        zIndex={1200}
                        className={`${showPage} top bouns_modal`}
                        closeModal={closeModal}
                        style={{ background: '#f5f5f5' }}
                        afterOpen={() => {
                            queryDiscountCouponList();
                        }}
                    >
                        <GrabEcoCreditsBonus
                            coupons={discountCouponList}
                            setShowPage={setShowPage}
                            visible={showPage === 'bouns_modal'}
                            refershData={() => {
                                queryIssuedRewards();
                                queryDiscountCouponList();
                            }}
                        />
                    </FullScreenModal>
                </EcoProvider>
            </PageLayout>
        </>
    );
};

export default EcoCredits;

// 以下两个方法不可删除，否则会重复触发 _app.js 中 getInitialProps 中的请求
export async function getServerSideProps({ locale, req }: GetServerSidePropsContext) {
    const whiteLocaleList = ['eu', 'de', 'fr', 'es', 'it', 'uk'];
    if (!whiteLocaleList.includes(locale as string)) {
        return redirectTo404();
    }

    const userInfoJson = req.cookies?.userInfo || '';
    let isLogin = false;
    try {
        const userInfo = JSON.parse(userInfoJson);
        isLogin = !!userInfo.token;
    } catch {}
    return {
        props: {
            isLogin,
        },
    };
}
